<template>
  <v-dialog
    class='credentials-filter-popup'
    :value="visiblePopup"
    :max-width="452"
    @click:outside="onNoClickButton()"
    @keydown.esc="onNoClickButton()"
  >
    <v-card class="popup-wrapper">
      <div class="title">{{$t(title)}}</div>

      <!-- Password length: a minimum, where 0 means "no length constraint".
           The live reading lives in the heading, so the slider needs no value
           box sitting off its baseline. -->
      <div class="section">
        <div class="section__heading">
          <span>{{$t('employees.select.credentials.length')}}</span>
          <span class="section__value">{{lengthHint}}</span>
        </div>
        <v-slider
          v-model="minLengthValue"
          :max="maxLength"
          :min="0"
          step="1"
          hide-details
          class="length-slider"
        ></v-slider>
        <div class="length-scale">
          <span>0</span>
          <span>{{maxLength}}</span>
        </div>
      </div>

      <div class="divider"></div>

      <!-- Character classes. On means the password must contain that class; off
           is not "must NOT contain", it simply does not filter. The whole row is
           the hit area, so the switch itself is read-only and purely visual. -->
      <div class="section">
        <div class="section__heading">
          <span>{{$t('employees.select.credentials.must.contain')}}</span>
        </div>
        <div
          class="class-row"
          :class="{ 'class-row--on': classValues[charClass.attr] }"
          v-for="charClass in characterClasses"
          :key="charClass.attr"
          @click="toggleClass(charClass.attr)"
        >
          <span class="class-row__label">{{$t(charClass.label)}}</span>
          <v-switch
            :input-value="classValues[charClass.attr]"
            color="primary"
            inset
            dense
            readonly
            hide-details
            class="class-row__switch"
          ></v-switch>
        </div>
      </div>

      <div class="buttons">
        <v-btn v-if='showNoButton' small depressed class="no-button btn-secondary" @click="onNoClickButton()">
          {{$t(noCaption)}}
        </v-btn>
        <v-btn small depressed class="yes-button btn-primary" @click="onYesClickButton()" :disabled='saveDisabled'>
          {{$t(yesCaption)}}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

const MAX_LENGTH = 64;

// Every class defaults to off, i.e. not filtered.
function defaultClassValues() {
  return {
    hasUppercase: false,
    hasLowercase: false,
    hasDigit: false,
    hasSpecial: false,
  };
}

export default {
  name: 'SelectCredentialsFilterPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    title: String,
    yesCaption: String,
    noCaption: String,
    showNoButton: {
      type: Boolean,
      default: true,
      required: false,
    },
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      minLengthValue: 0,
      classValues: defaultClassValues(),
    };
  },
  watch: {
    // Reset on every open/close, matching the other filter popups.
    visiblePopup() {
      this.resetSelection();
    },
  },
  methods: {
    resetSelection() {
      this.minLengthValue = 0;
      this.classValues = defaultClassValues();
    },
    toggleClass(attr) {
      this.classValues = {
        ...this.classValues,
        [attr]: !this.classValues[attr],
      };
    },
    onYesClickButton() {
      // Emit only what the operator actually narrowed: a length of 0 and an
      // off switch are not constraints, so they are left out rather than sent
      // as no-op chips.
      const selection = {};

      if (this.lengthNarrowed) {
        selection.passwordLength = `> ${this.minLengthValue}`;
      }

      Object.keys(this.classValues).forEach(attr => {
        if (this.classValues[attr]) {
          selection[attr] = 'yes';
        }
      });

      this.$emit('clicked', selection);
      this.resetSelection();
    },
    onNoClickButton() {
      this.$emit('clicked', false);
      this.resetSelection();
    },
  },
  computed: {
    maxLength() {
      return MAX_LENGTH;
    },
    lengthNarrowed() {
      return this.minLengthValue > 0;
    },
    // Says out loud what the slider currently means, so a 0 default does not
    // read as a broken filter.
    lengthHint() {
      return this.lengthNarrowed
        ? this.$t('employees.select.credentials.length.min', { count: this.minLengthValue })
        : this.$t('employees.select.credentials.length.any');
    },
    characterClasses() {
      return [
        { attr: 'hasUppercase', label: 'employees.select.credentials.uppercase' },
        { attr: 'hasLowercase', label: 'employees.select.credentials.lowercase' },
        { attr: 'hasDigit', label: 'employees.select.credentials.digits' },
        { attr: 'hasSpecial', label: 'employees.select.credentials.special' },
      ];
    },
    // Nothing narrowed means nothing to apply.
    saveDisabled() {
      const classNarrowed = Object.keys(this.classValues)
        .some(attr => this.classValues[attr]);
      return !this.lengthNarrowed && !classNarrowed;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.popup-wrapper {
  max-width: rem(452);
  min-width: rem(452);
  padding: $space-7 $space-8 $space-6;

  .title {
    color: var(--text-primary);
    font-family: $font-family-semibold;
    font-size: $font-size-base !important;
    letter-spacing: rem(1.12);
    line-height: $space-4;
    text-transform: uppercase;
    padding-bottom: $space-5;
  }

  .section {
    &__heading {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      color: var(--text-secondary);
      font-family: $font-family-semibold;
      font-size: $font-size-xs;
      letter-spacing: rem(0.8);
      text-transform: uppercase;
      margin-bottom: $space-1;
    }

    // The live reading of the slider, promoted next to its heading so the
    // number is never orphaned off the slider's baseline.
    &__value {
      color: var(--text-primary);
      font-family: $font-family-medium;
      font-size: $font-size-sm;
      letter-spacing: 0;
      text-transform: none;
      transition: color 160ms ease;
    }
  }

  .length-scale {
    display: flex;
    justify-content: space-between;
    margin-top: rem(-6);
    color: var(--text-tertiary);
    font-size: $font-size-xs;
    user-select: none;
  }

  .divider {
    height: $border-width-thin;
    background-color: var(--border-primary);
    margin: $space-5 0;
  }

  // Borderless rows: the tint and the switch carry the state, so four stacked
  // outlines are not needed.
  .class-row {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    // Fixed height keeps every row identical regardless of what the switch's
    // internals measure.
    min-height: rem(40);
    padding: 0 $space-3;
    margin-bottom: $space-0-5;
    border-radius: $radius-6;
    background-color: transparent;
    cursor: pointer;
    overflow: hidden;
    transition: background-color $transition-duration-fast $transition-timing-ease-out;

    &:last-child {
      margin-bottom: 0;
    }

    // Accent bar that grows from the vertical centre on hover, and stays for
    // the selected state. Transform-only, so it costs no layout.
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: rem(2);
      background-color: var(--brand-primary);
      transform: scaleY(0);
      transition: transform $transition-duration-fast $transition-timing-ease-out;
    }

    &:hover {
      background-color: var(--bg-secondary);

      &:before {
        transform: scaleY(0.55);
      }

      .class-row__label {
        transform: translateX(#{rem(2)});
      }
    }

    &--on {
      background-color: var(--brand-primary-bg);

      &:before {
        transform: scaleY(1);
      }

      .class-row__label {
        color: var(--brand-primary);
        font-family: $font-family-medium;
      }
    }

    &__label {
      color: var(--text-primary);
      font-family: $font-family-regular;
      font-size: $font-size-sm;
      user-select: none;
      transition: color $transition-duration-fast $transition-timing-ease-out,
        transform $transition-duration-fast $transition-timing-ease-out;
    }

    // Vuetify ships selection controls with margin-top 16px / padding-top 4px
    // and a 34px ripple, which drops the switch ~9px below the label's centre
    // line and inflates the row. Strip it so the row's flex centring wins.
    // ::v-deep is required: the dialog content is detached to [data-app], so a
    // plain descendant selector on the dialog's own class never matches it.
    ::v-deep .v-input,
    ::v-deep .v-input--switch,
    ::v-deep .v-input--selection-controls {
      margin: 0 !important;
      padding: 0 !important;
      flex: 0 0 auto;
    }

    ::v-deep .v-input__control,
    ::v-deep .v-input__slot {
      margin: 0 !important;
      padding: 0 !important;
      min-height: 0 !important;
    }

    // The row owns the click, so the switch adds no hit area of its own.
    // The 1px nudge corrects Vuetify's 22px track sitting at top:0 inside a
    // 24px box, which leaves the thumb a pixel above the label's centre line.
    // It has to be a transform: a margin would be absorbed by the flex
    // centring, which centres the margin box rather than the content box.
    ::v-deep .v-input--selection-controls__input {
      margin: 0 !important;
      transform: translateY(1px);
      pointer-events: none;
    }

    ::v-deep .v-input--selection-controls__ripple {
      display: none !important;
    }
  }

  .buttons {
    text-align: right;
    padding-top: $space-6;

    .no-button {
      box-sizing: border-box;
      height: $btn-height-md;
      width: rem(86);
      border: $border-width-thin solid var(--brand-primary);
      color: var(--brand-primary);
      border-radius: $radius-sm;
      background-color: var(--bg-primary);
      box-shadow: $shadow-sm;
    }

    .yes-button {
      height: $btn-height-md;
      width: rem(51);
      border-radius: $radius-sm;
      box-shadow: $shadow-sm;
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: $space-4;
      text-align: center;
      margin-left: $space-2;
    }
  }
}
</style>
